import React, { useState, useEffect } from 'react';
import { teamApi } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  UserGroupIcon,
  TrashIcon,
  XMarkIcon,
  EnvelopeIcon,
} from 'components/icons';
import PageHeader from '../../components/ui/PageHeader';

interface TeamMember {
  id: string;
  role: string;
  joinedAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface TeamData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  members: TeamMember[];
  createdAt: string;
}

const ROLES = [
  { value: 'ADMIN', label: 'Admin', description: 'Can invite and remove members' },
  { value: 'MANAGER', label: 'Manager', description: 'Can manage resources' },
  { value: 'MEMBER', label: 'Member', description: 'Standard access' },
  { value: 'VIEWER', label: 'Viewer', description: 'Read-only access' },
];

const Team: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<TeamData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await teamApi.getAll();
      setTeams(response.data.data?.teams || response.data.teams || []);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreatingTeam(true);
    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;
    const description = formData.get('description') as string;

    try {
      await teamApi.create({ name, description });
      await fetchTeams();
      setShowCreateForm(false);
      toast.success('Team created');
      (e.target as HTMLFormElement).reset();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleInvite = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (teams.length === 0) return;
    setInviting(true);
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const role = formData.get('role') as string;

    try {
      await teamApi.inviteMember(teams[0].id, { email, role });
      await fetchTeams();
      setShowInviteForm(false);
      toast.success(`Invitation sent to ${email}`);
      (e.target as HTMLFormElement).reset();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to invite member');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (teamId: string, memberId: string) => {
    setRemovingId(memberId);
    try {
      await teamApi.removeMember(teamId, memberId);
      await fetchTeams();
      toast.success('Member removed');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    } finally {
      setRemovingId(null);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'OWNER': return 'bg-brand-100 text-brand-700';
      case 'ADMIN': return 'bg-purple-100 text-purple-700';
      case 'MANAGER': return 'bg-blue-100 text-blue-700';
      case 'MEMBER': return 'bg-surface-sunk text-content';
      case 'VIEWER': return 'bg-surface-sunk text-content-muted';
      default: return 'bg-surface-sunk text-content';
    }
  };

  const currentTeam = teams[0]; // Primary team

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <PageHeader
        title="Team"
        subtitle="Manage your team members and permissions"
      />
        <div className="flex items-center gap-2">
          {currentTeam && (
            <button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              {showInviteForm ? <XMarkIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4" />}
              {showInviteForm ? 'Cancel' : 'Invite Member'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-surface shadow rounded-lg px-6 py-8 text-center text-content-muted">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-4"></div>
          Loading...
        </div>
      ) : teams.length === 0 ? (
        /* No team yet — create one */
        <div className="bg-surface shadow rounded-lg p-8">
          {showCreateForm ? (
            <form onSubmit={handleCreateTeam} className="max-w-md mx-auto space-y-4">
              <h3 className="text-title text-content">Create Your Team</h3>
              <div>
                <label className="label">Team Name *</label>
                <input name="name" required className="input" placeholder="e.g. Acme GovCon Team" />
              </div>
              <div>
                <label className="label">Description</label>
                <input name="description" className="input" placeholder="What does this team work on?" />
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={creatingTeam} className="btn-primary disabled:opacity-50">
                  {creatingTeam ? 'Creating...' : 'Create Team'}
                </button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="text-sm text-content-muted hover:text-content">
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <UserGroupIcon className="h-12 w-12 mx-auto text-content-subtle mb-4" />
              <h3 className="text-title text-content mb-2">No team yet</h3>
              <p className="text-content-muted mb-6 max-w-md mx-auto">
                Create a team to invite colleagues, assign opportunities, and collaborate on proposals.
              </p>
              <button onClick={() => setShowCreateForm(true)} className="btn-primary flex items-center gap-2 mx-auto">
                <PlusIcon className="h-5 w-5" />
                Create Your Team
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Team Info */}
          <div className="bg-surface shadow rounded-lg px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-title text-content">{currentTeam.name}</h2>
              {currentTeam.description && (
                <p className="text-sm text-content-muted mt-0.5">{currentTeam.description}</p>
              )}
            </div>
            <span className="text-sm text-content-subtle">{currentTeam.members.length} member{currentTeam.members.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Invite Form */}
          {showInviteForm && (
            <div className="bg-surface shadow rounded-lg p-6">
              <h3 className="font-medium text-content mb-4">Invite a Team Member</h3>
              <form onSubmit={handleInvite} className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="label">Email Address</label>
                  <div className="relative">
                    <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-subtle" />
                    <input name="email" type="email" required className="input pl-10" placeholder="colleague@company.com" />
                  </div>
                </div>
                <div className="w-40">
                  <label className="label">Role</label>
                  <select name="role" className="input" defaultValue="MEMBER">
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
                <button type="submit" disabled={inviting} className="btn-primary disabled:opacity-50 whitespace-nowrap">
                  {inviting ? 'Sending...' : 'Send Invite'}
                </button>
              </form>
            </div>
          )}

          {/* Members List */}
          <div className="bg-surface shadow rounded-lg">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="font-medium text-content">Team Members</h3>
            </div>
            <div className="divide-y divide-border">
              {currentTeam.members.map((member) => {
                const isCurrentUser = member.user.id === user?.id;
                const isOwner = member.role === 'OWNER';

                return (
                  <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-surface-sunk">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-brand-700 font-medium text-sm">
                          {member.user.firstName?.[0]}{member.user.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-content">
                          {member.user.firstName} {member.user.lastName}
                          {isCurrentUser && <span className="text-xs text-content-subtle ml-2">(you)</span>}
                        </p>
                        <p className="text-xs text-content-muted">{member.user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize ${getRoleColor(member.role)}`}>
                        {member.role.toLowerCase()}
                      </span>
                      <span className="text-xs text-content-subtle">
                        Joined {new Date(member.joinedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </span>
                      {!isOwner && !isCurrentUser && (
                        <button
                          onClick={() => handleRemoveMember(currentTeam.id, member.id)}
                          disabled={removingId === member.id}
                          className="p-1.5 text-content-subtle hover:text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                          title="Remove member"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Team;
